import { Component } from '@angular/core';
import {
  Campaign,
  CampaignSection,
  CampaignSummary,
  CustomDomain,
  DeploymentLink, LifelinePhoneSetting,
  Pager,
  Phone,
  PhoneStatus,
  PhoneStatusLabels,
  SessionType,
  SessionTypeLabels,
  getLinkTargetPath,
  buildDeploymentLinkUrl,
} from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  DeploymentLinkService,
  LoaderService,
  PhoneService,
  SideNavService,
} from '../../_services';
import { FormBuilder } from '@angular/forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { copyToClipboard } from '../../_helpers';
import { forkJoin } from 'rxjs';
import { config } from '../../../config/config';
import { CrudGetComponent } from '../../_directives';
import { expandCollapse } from '../../_shared';

@Component({
  templateUrl: './campaign-deployment-page.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-deployment-page.component.css'],
  animations: [expandCollapse],
})
export class CampaignDeploymentPageComponent extends CrudGetComponent {
  public section = CampaignSection.DeploymentPage;
  public selectedDomain: CustomDomain = null;
  campaign: Campaign;
  summary: CampaignSummary;
  phones: Phone[];
  deploymentLinks: DeploymentLink[];
  domainsMap = {};
  copied: boolean = false;
  selectedLink: DeploymentLink = null;
  sections = [
    {
      label: 'Phone',
      isPhone: true,
      collapsed: true,
      columns: ['Phone Number', 'Domain', 'Status', 'Date Created'],
      canCopy: true,
      containerClass: 'phones__table',
    },
    {
      label: 'Domain',
      isDomain: true,
      collapsed: true,
      columns: ['Domain', 'Type', 'Status', 'Date Verified'],
      canCopy: false,
      containerClass: 'domains__table',
    },
    {
      collapsed: true,
      isDeploymentLink: true,
      label: 'Deployment Link',
      newButtonLabel: 'Link',
      canCopy: false,
      columns: ['Name', 'Status', 'Type', 'Target Page', 'Source Type', 'URL', 'Last Modified'],
      containerClass: 'links__table',
    },
  ];

  copyValue = copyToClipboard;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected sideNav: SideNavService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected phoneService: PhoneService,
    private deploymentLinkService: DeploymentLinkService
  ) {
    super(router, location, route, campaignService, alertService);
    this.sideNav.setSideNavExpanded(false);
  }

  ngOnInit() {
    super.ngOnInit();
    this.campaign = this.campaignService.getCurrentCampaign();
    this.id = this.campaign.id;
    this.onCampaignFetch(this.campaign);
  }

  onCampaignFetch(campaign: Campaign) {
    (this.campaign.custom_domains || []).forEach((domain) => {
      this.domainsMap[domain.id.toString()] = domain;
    });
    this.loaderService.show();
    forkJoin([
      this.campaignService.summary(campaign.id.toString()),
      this.phoneService.list({
        domain__in: campaign.custom_domains.map((domain: CustomDomain) => domain.id).join(','),
        page: 1,
        page_size: config.maxPageSize,
      }),
      this.deploymentLinkService.list({ campaign: campaign.id.toString(), page: 1, page_size: config.maxPageSize }),
    ]).subscribe(
      (data: [CampaignSummary, Pager, Pager]) => {
        if (data) {
          this.summary = data[0];
          this.phones = data[1].results;
          this.deploymentLinks = data[2].results;

          if (this.phones && this.phones.length > 0) {
            // Expand phone section if there are phones
            this.sections[0].collapsed = false;
          }
        }
        this.loading = false;
        this.loaderService.hide();
      },
      (error) => {
        this.loaderService.hide();
        this.handleError(error);
      }
    );
  }

  get crms() {
    return (
      this.summary && (this.summary.crms.length > 1 ? `${this.summary.crms.length} crms` : this.summary.crms.join(','))
    );
  }

  get domains() {
    return (this.campaign && this.campaign.custom_domains) || [];
  }

  get marketplaceDomain() {
    return this.domains.find((domain) => domain.type === SessionType.Marketplace);
  }

  get paymentUpdaterDomain() {
    return this.domains.find((domain) => domain.type === SessionType.PaymentUpdate);
  }

  get customerPortalDomain() {
    return this.domains.find((domain) => domain.type === SessionType.CustomerPortal);
  }

  get supportPortalDomain() {
    return this.domains.find((domain) => domain.type === SessionType.SupportPortal);
  }

  get helpDeskDomain() {
    return this.domains.find((domain) => domain.type === SessionType.HelpDesk);
  }

  getWidth(section) {
    if (section.isDeploymentLink) {
      return '10%';
    }
    return (100 / ((section.columns || []).length - 1)).toFixed(2) + '%';
  }

  hasData(section) {
    if (section.isPhone) {
      return this.phones && !!this.phones.length;
    } else if (section.isDomain) {
      return this.campaign.custom_domains && !!this.campaign.custom_domains.length;
    } else if (section.isDeploymentLink) {
      return this.deploymentLinks && !!this.deploymentLinks.length;
    }
    return false;
  }

  getDomainType(domain: CustomDomain) {
    return SessionTypeLabels[domain.type];
  }

  getLinkType(deploymentLink: DeploymentLink) {
    return SessionTypeLabels[deploymentLink.link_type];
  }

  getPhoneStatus(phone: Phone) {
    return PhoneStatusLabels[phone.status];
  }

  getPhoneStatusClass(phone: Phone) {
    switch (phone.status) {
      case PhoneStatus.Active:
      case PhoneStatus.PortedOut:
        return 'active';
      case PhoneStatus.Deleted:
      case PhoneStatus.Failed:
        return 'inactive';
      default:
        return 'pending';
    }
  }

  getLinkTargetPath(link: DeploymentLink) {
    return getLinkTargetPath(link);
  }

  getLinkTrackingSource(link: DeploymentLink) {
    return (
      link.tracking_source && link.tracking_source['source'] && link.tracking_source['source'].split('-').join(' ')
    );
  }

  toggleSection(selectedIndex: number) {
    this.sections.forEach((section, index) => {
      if (index === selectedIndex) {
        section.collapsed = !section.collapsed;
      } else {
        section.collapsed = true;
      }
    });
  }

  openDocumentation(domain: CustomDomain, link: DeploymentLink) {
    this.selectedDomain = domain;
    this.selectedLink = link;
    this.modalService.getModal('deploymentDocumentDialog').open();
  }

  openQRCodeDialog(link: DeploymentLink) {
    this.selectedLink = link;
    this.modalService.getModal('QRCodeDialog').open();
  }

  handleClick(section) {
    if (section.isPhone) {
      this.router.navigateByUrl('/phone');
    } else if (section.isDomain) {
      this.createDomain();
    } else if (section.isDeploymentLink) {
      this.createLink();
    }
  }

  protected createDomain() {
    this.selectedDomain = {
      id: null,
      campaign: this.campaign.id,
      domain: null,
      is_verified: false,
      verified: null,
      cname_target: null,
      type: null,
      name: null,
      visual_entry_funnels: [],
      show_phone_link: LifelinePhoneSetting.Never,
      created: null,
      support_phone: null,
      is_react: false,
    };
    this.modalService.getModal('deploymentDomainDialog').open();
  }

  onDomainUpdated(domain: CustomDomain) {
    for (let i = 0; i < this.campaign.custom_domains.length; i++) {
      if (this.campaign.custom_domains[i].id === domain.id) {
        this.campaign.custom_domains[i] = domain;
        break;
      }
    }
  }

  onSaveDomain(domain: CustomDomain) {
    if (this.selectedDomain && this.selectedDomain.id) {
      this.onDomainUpdated(domain);
    } else {
      this.campaign.custom_domains.push(domain);
    }
    this.modalService.getModal('deploymentDomainDialog').close();
  }

  protected createLink() {
    this.selectedLink = null;
    this.modalService.getModal('deploymentLinksDialog').open();
  }

  protected onLinkUpdated(deploymentLink: DeploymentLink) {
    for (let i = 0; i < this.deploymentLinks.length; i++) {
      if (this.deploymentLinks[i].id === deploymentLink.id) {
        this.deploymentLinks[i] = deploymentLink;
        break;
      }
    }
  }

  onSaveLink(deploymentLink: DeploymentLink) {
    if (this.selectedLink) {
      this.onLinkUpdated(deploymentLink);
    } else {
      this.deploymentLinks.push(deploymentLink);
    }

    this.modalService.getModal('deploymentLinksDialog').close();
  }

  downloadQRCodeImage() {
    if (this.selectedLink) {
      let link = document.createElement('a');
      document.documentElement.append(link);
      const img = document.querySelector('.qrcode-container img');
      link.setAttribute('download', `${this.selectedLink.name}_qrcode.jpg`);
      link.href = img['src'];
      // Auto click the link
      link.click();
      link.remove();
    }
  }

  openQRCode() {
    const img = document.querySelector('.qrcode-container img');
    const base64Image = img['src'];
    // Convert Base64 to Blob
    const byteString = atob(base64Image.split(',')[1]); // Decode Base64
    const mimeType = base64Image.split(',')[0].match(/:(.*?);/)[1]; // Extract MIME type
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: mimeType });

    // Create an Object URL
    const objectURL = URL.createObjectURL(blob);

    // Open the image in a new tab
    window.open(objectURL, '_blank');
  }

  toggleDropdown(selectedDeploymentLink: DeploymentLink, event?: any) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.deploymentLinks.forEach((deploymentLink: DeploymentLink) => {
      if (selectedDeploymentLink && selectedDeploymentLink.id === deploymentLink.id) {
        deploymentLink['showDropdown'] = !deploymentLink['showDropdown'];
      } else {
        deploymentLink['showDropdown'] = false;
      }
    });
  }

  editLink(event: Event, deploymentLink: DeploymentLink) {
    event.stopPropagation();
    event.preventDefault();
    this.selectedLink = deploymentLink;
    this.modalService.getModal('deploymentLinksDialog').open();
    this.toggleDropdown(null);
  }

  deleteLink(deploymentLink: DeploymentLink) {
    if (window.confirm('Are you sure you want to delete this deployment link?')) {
      this.loaderService.show();
      this.deploymentLinkService.delete(deploymentLink.id).subscribe(
        () => {
          this.alertService.success('Deleted deployment link.');
          this.loaderService.hide();
          this.deploymentLinks = this.deploymentLinks.filter((link) => link.id !== deploymentLink.id);
        },
        (error) => {
          this.loaderService.hide();
          this.handleError(error);
        }
      );
      this.toggleDropdown(null);
    }
  }

  get brandConfiguredImageName() {
    if (this.campaign && this.campaign.active_domain && this.campaign.styles && this.campaign.styles.length > 0) {
      if (
        this.campaign.is_hybrid &&
        (!this.campaign.hybrid_entry_funnels || !this.campaign.hybrid_entry_funnels.length)
      ) {
        return 'filled';
      } else if (this.campaign.visual_entry_funnels && this.campaign.visual_entry_funnels.length > 0) {
        return 'filled';
      }
    }
    return 'outline';
  }

  get supportConfiguredImageName() {
    if (
      this.campaign &&
      this.campaign.support_phone &&
      this.campaign.call_center_hours &&
      this.campaign.call_center_hours.length > 0
    ) {
      return 'filled';
    }
    return 'outline';
  }

  get productConfiguredImageName() {
    if (this.campaign && this.campaign.campaign_products && this.campaign.campaign_products.length > 0) {
      return 'filled';
    }
    return 'outline';
  }

  get helpDeskConfiguredImageName() {
    if (this.campaign && this.campaign.chatbot_enabled && this.campaign.faq_topic) {
      return 'filled';
    }
    return 'outline';
  }

  getComputedLinkUrl(link: DeploymentLink) {
    return buildDeploymentLinkUrl(link, this.campaign && this.campaign.active_domain, this.campaign && this.campaign.ticket_system);
  }

  onLinkSaveComplete(data) {
    if (data) {
      const index = this.deploymentLinks.findIndex((link) => link.id === data.id);
      if (index > -1) {
        this.deploymentLinks[index] = { ...data };
      } else {
        this.deploymentLinks.unshift(data);
      }
    }
    this.selectedLink = null;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.sideNav.setSideNavExpanded(true);
  }
}
